<template>
  <div :class="{ 'c-dark-theme': $store.state.darkMode, animated: false, fadeIn: true }">
    <CCard :style="tankCardStyle(450, 500)" :border-color="signalVariant(signalColor)">
      <CCardHeader>
        <TankCardHeader :tank="tank"
                        :signal-color="signalColor"
                        :status-variant="statusVariant"
                        :status-msg="statusMsg"
                        :last-dt="lastDt"
                        :inout="inout"
                        :show-inout="tank.tankUse==='1' || tank.tankUse==='2'"
                        @inout="setInout">
          <template #units>
            <TankUnitBadges :vol-unit="volUnit" :len-unit="lenUnit" :tmp-unit="tmpUnit"
                            @toggle-vol="toggleVolUnit" @toggle-len="toggleLenUnit" @toggle-tmp="toggleTmpUnit"/>
          </template>
        </TankCardHeader>
      </CCardHeader>

      <!-- 좌우 여백을 줄여 캔버스는 왼쪽, 상세표는 오른쪽 끝에 붙인다 -->
      <CCardBody class="px-2">
      <!-- 입출고 표시는 탱크 캔버스 위에 겹쳐 띄운다 (position-relative 필요) -->
      <!--
        three.js 캔버스가 카드 본문 폭 전체를 쓴다. 이 div 가 캔버스가 붙는 곳이자 오버레이의 기준 상자다.
        (mounted() 가 이 요소의 clientWidth 로 렌더러 폭을 정한다)
        오버레이: 상단 입출고(InoutProgress), 하단 경보 배지(WidgetEvents), 오른쪽 상세표(TankDetailTable)
      -->
      <div ref="container"
           class="position-relative w-100 d-flex align-items-center justify-content-start"
           style="overflow: hidden">
        <!-- 발생한 경보 배지. 탱크 오른쪽 가운데에서 세로로 쌓아 표시한다. 상세표 레이어 안쪽 여백(라벨 왼쪽 빈 공간)까지 붙인다 -->
        <WidgetEvents ref="evtWidget" vertical class="position-absolute evt-overlay"
                      :style="{ top: '50%', transform: 'translateY(-50%)', right: `${detailW - 12}px`, zIndex: 11 }"/>
        <!-- 충족률. 탱크 그림 왼쪽 아래에 레이어로 표시 (상단 버튼 그룹에서 옮김) -->
        <div class="tvp-overlay tvp-overlay-xl" :class="{'font-lg': $store.state.largeFont}">{{ comma(pk.tvp, 0) }} %</div>
        <!-- 입출고 오버레이는 오른쪽 상세표 폭만큼 비운다 -->
        <InoutProgress :inout="inout"
                       :vol-percent="volPercent"
                       :diff-ovm="diffOvm"
                       :diff-ovc="diffOvc"
                       :vol-unit="volUnit"
                       :target-vol.sync="inputTargetVol"
                       :style="{ right: `${detailW}px` }"
                       @save="setInoutVolume"
                       @clear="setInout('N')"/>
        <!-- 상세표는 캔버스 오른쪽 위에 레이어로 띄운다 -->
        <div class="position-absolute" :style="{ top: 0, right: 0, width: `${detailW}px`, zIndex: 12 }">
          <TankDetailTable :pk="pk"
                            :tank="tank"
                            :status="status"
                            :vol-unit="volUnit"
                            :len-unit="lenUnit"
                            :tmp-unit="tmpUnit"
                            show-vacancy
                            outline-labels
                            @toggle-detail="toggleDetail"/>
        </div>
      </div>
      <TankTempTable v-show="detailTmShow" :pk="pk" :tmp-unit="tmpUnit" :tm-use-map="tmUseMap"/>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
//------------------------------------------------------------------------
/**
 * Tank3dHorizontalCylinder - 주유소 지하탱크(가로 실린더) 3D 뷰
 *
 * Tank3D 의 카드 레이아웃·패킷 처리·입출고 로직은 그대로 두고 장면만 다르다.
 *   - 탱크 : 참고 사진(examples/tank3.png)처럼 원통 + 둥근 경판 + 맨홀·노즐 + 새들 지지대.
 *   - 개방 : Tank2D 처럼 카메라 쪽 끝면(경판)을 떼어 내부가 보이게 한다. 열린 끝으로
 *            유량·수분의 단면(원형 조각)과 안쪽으로 이어지는 유면이 보인다.
 *   - 유량 : CSG 대신 수위 클리핑 평면으로 자른다. 패킷마다 평면 상수값만 바꾸므로
 *            지오메트리를 다시 만들지 않는다 (유면판만 폭이 달라 다시 만든다).
 *   - 렌더링 : Tank3D 와 같이 매 프레임 그리지 않고 바뀔 때만 render() 한다.
 *
 * 적용 대상 : tankShape '2'(실린더) + tankUse '6'(주유소) + isTwin (TankMonitor 에서 분기)
 */
import WidgetEvents from '../widget/WidgetEvents'
import InoutProgress from './InoutProgress'
import TankCardHeader from './TankCardHeader'
import TankDetailTable from './TankDetailTable'
import TankUnitBadges from './TankUnitBadges'
import TankTempTable from './TankTempTable'
import InoutStatusMixin from '@/common/inout-status-mixin'
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js'
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js'
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js'
import {PacketSchema} from "@/common/schema";
import {
  getHexColor,
  clone,
  comma,
  trans,
  apiCall,
  unitSign,
  speech,
  getDiffMinute,
  ltrToUnit,
  unitToLtr
} from "@/common/utils";
import {IoStsMap} from "@/common/constants";
import moment from "moment";

const PI = Math.PI;
const _normalBadge = {'1':'dark', '2':'dark', '3':'dark', '4':'dark', '5':'dark', '6':'dark', '7':'dark', '8':'dark', '9':'dark'};

/** 클리핑 평면 상수. Plane(normal, constant) 는 dot(normal,p)+constant >= 0 인 쪽을 남긴다. */
const keepBelowY = (y) => new THREE.Plane(new THREE.Vector3(0, -1, 0), y);   // y <= y0
const keepAboveY = (y) => new THREE.Plane(new THREE.Vector3(0, 1, 0), -y);   // y >= y0

export default {
  name: "Tank3dHorizontalCylinder",
  components: {
    WidgetEvents,
    InoutProgress,
    TankCardHeader,
    TankDetailTable,
    TankUnitBadges,
    TankTempTable
  },
  mixins: [InoutStatusMixin],
  props: {
    width: {type: Number, default: 300},
    height: {type: Number, default: 200},
    tank: { type: Object, default: null },
    bgColor: { type: String, default: '#FFFFFF'},
    tlgPacket : { type: Object, default: null },
    initPacket : { type: Object, default: null }
  },
  data () {
    return {
      lastDt: '',
      tnm: '',
      tid: '',
      detailTmShow: false,
      // 작업 없음은 'N' 이다. null 로 두면 applyInoutSts()가 '미확인'으로 표시한다.
      inout: { sts: 'N' },
      events: {data: [], dt: null},

      trans: trans,
      unitSign: unitSign,
      comma: comma,
      volUnit: 'l', // <--> g
      lenUnit: 'mm', // <--> i
      tmpUnit: 'c', // <---> f

      badgeColor: clone(_normalBadge),
      status: 'off', // norm, warn, info, off
      signalColor: 'dark',
      pk: clone(PacketSchema),
      w: 400,
      h: 250,


      oilColors: this.$store.state.codeMaps['OIL_COLOR'],
      nozSizeMap: this.$store.state.codeMaps['NOZ_SIZE'],
      alarmCfg: this.$store.state.alarm,

      machine: null,
      tankColor: null,
      oilColor: 0,
      colors: {
        lining: '#6b7280',   // 내벽 (어두운 회색보다 한 단계 밝은 중간 회색)
        support: '#a9a59d',  // 받침대 (콘크리트: 따뜻한 밝은 회색, 알갱이 텍스처를 얹는다)
        water: '#367DB0',    // Tank2D 와 같은 수분색
        pipe: '#F0F0F0',
        floatOil: '#C8CCD2', // 유량 플로트 (금속색, metalness 로 광택)
        floatWtr: '#1B2A5B', // 수분 플로트 (네이비)
        sensor: {
          off: '#606050',
          on: '#16A2B8',
          warn: '#FFC008',
          info: '#047BFF',
          danger: '#DC3545',
          error: '#FF0000',
          unknown: '#FF00FF'
        },
        tm: {
          off: '#001233',
          on: '#FF00FF',
          danger: '#DC3545',
          error: '#FF0000',
          unknown: '#FF00FF',
        }
      },
      tmUseMap: {
        'tm1': false, 'tm2': false, 'tm3': false,
        'tm4': false, 'tm5': false, 'tm6': false,
        'tm7': false, 'tm8': false, 'tm9': false,
      },

      openX: 0, // 열린 끝(카메라 쪽 경판 자리)의 x 좌표
      pipeX: 0, // 센서 배관 x 좌표 (온도계도 같은 자리)
      floatH: 0.03, // 플로트 원판 두께 (m)

      senH: 3,    // 센서 길이 (m)
      tankL: 6,   // 탱크 길이 (m) - tankWidth
      tankR: 1.2, // 탱크 반지름 (m) - tankHeight/2
      wallT: 0.05,// 벽 두께 (m)
      fluidOpacity: 0.45, // 유량 불투명도. 낮을수록 투명하다
      wtrOpacity: 0.85,   // 수분 불투명도. 유량보다 진하게 두어 아래 깔린 층이 또렷이 보이게 한다
      nozSize: 0.05,

      inputTargetVol: null,
      widgetInstance: null,
    }
  },
  created(){
    this.volUnit = this.tank.unitOfVol?this.tank.unitOfVol:'l'
    this.lenUnit = this.tank.unitOfLen?this.tank.unitOfLen:'mm'
    this.tmpUnit = this.tank.unitOfTmp?this.tank.unitOfTmp:'c'

    // three.js 객체는 data() 에 두지 않는다.
    // data() 에 두면 Vue 가 씬 그래프·지오메트리·재질 전체를 깊은 반응형으로 훑어
    // 탱크 하나당 수만 개의 getter/setter 를 만들고, 카드가 여러 장이면 화면이 멈춘다.
    // created() 에서 붙인 속성은 반응형이 아니다.
    this.renderer = null;
    this.scene = null;
    this.camera = null;
    this.controls = null;
    this.container = null;
    this.geo = {};      // 지오메트리 모음 (destroyObjects 에서 일괄 반납)
    this.mat = {};      // 재질 모음
    this.mesh = {};     // 씬에 올린 메쉬 모음
    this.tex = {};      // 텍스처 모음 (destroyObjects 에서 반납)
    this.tmMater = {};  // 온도계 재질
    this.tmMesh = {};   // 온도계 메쉬
    // 수위 클리핑 평면 - 패킷마다 constant 만 갱신한다
    this.camTarget = null; // 초기 카메라 시선점 (init 에서 계산)
    this.lvl = {
      oilTop: null,  // 유면 (y <= oilY)
      oilBtm: null,  // 유량 하한 = 수면 (y >= wtrY)
      wtrTop: null,  // 수면 (y <= wtrY)
    };
  },

  computed: {
    // 오른쪽 상세표 레이어 폭 (px). 표 폭(약 150px)에 맞추고 큰 글씨 모드면 같이 키운다
    detailW(){
      return this.$store.state.largeFont ? 195 : 155;
    },
  },
  async mounted() {
    try{
      // 렌더러 폭이 칼럼보다 크면 우측 상세표를 침범하므로 컨테이너 폭에 맞춰 줄인다 (종횡비 유지)
      // 렌더러 폭은 컨테이너(카드 본문) 폭을 그대로 쓰고, 높이는 height 프롭을 쓴다.
      // 상세표가 오른쪽에 레이어로 겹치므로 탱크는 남은 왼쪽 영역에 맞춰 그린다 (init 의 카메라 참조)
      const availW = this.$refs.container ? this.$refs.container.clientWidth : 0;
      this.w = availW > 0 ? availW : this.width;
      this.h = this.height;

      if( this.tank ) {
        this.tnm = this.tank.name;
        this.init();
        await this.getInout();
      }

      if( Array.isArray( this.$refs['evtWidget'] ) ) {
        this.widgetInstance = this.$refs['evtWidget'][0];
      } else {
        this.widgetInstance = this.$refs['evtWidget'];
      }

      if(this.initPacket) this.renderVolume( this.initPacket );

      // 최근 탱크 재고
      try{
        const r = await apiCall('get', `/api/tank/latest-packet/${this.tank.tid}`);
        if(r.code===200 && r.result ){
          this.setVolume({payload: r.result}, true);
          this.renderVolume( r.result );
          this.lastDt = this.toHumanTime( getDiffMinute( moment(r.result.dt), moment() )) + ' 전';
        }
      }catch(err){
        console.error(err);
      }
    }catch(err){
      console.error(err);
    }
  },

  methods: {

    /**
     * 씬 초기화. 탱크 치수는 Tank3D 의 실린더형(2)과 같은 규칙으로 읽는다.
     *   tankR = tankHeight/2, tankL = tankWidth (mm → m)
     */
    init() {
      const tank = this.tank;
      this.tid = tank.tid;
      this.machine = tank.machine || null;
      if( !this.machine ){
        console.error(`--- init Tank3dHorizontalCylinder - TID : [${tank.tid}] Machine Not found`);
      }

      // 지름은 tankHeight 를 쓴다. 비어 있으면 센서 길이(실측값)로, 그것도 없으면 2400mm 로 폴백한다.
      // 센서봉은 보통 내경보다 길어(라이저 관통) 지름으로 쓰기엔 크지만, 상수보다는 탱크에 가깝다.
      const sensorLen = Number(this.machine?.sensorLen) || 0;
      const diameter = Number(tank.tankHeight) || sensorLen || 2400;
      this.tankR = Math.max( 0.3, diameter / 2 / 1000 );
      this.tankL = Math.max( 0.6, (Number(tank.tankWidth) || 6000) / 1000 );
      this.senH  = sensorLen ? sensorLen / 1000 : this.tankR * 2 + 0.5;
      this.wallT = Math.max( 0.03, this.tankR * 0.035 );

      const nozStr = this.nozSizeMap?.[ tank.nozSize ] || '';
      const noz = Number( String(nozStr).replace(/[^0-9]/g, "") );
      this.nozSize = noz > 0 ? noz / 1000 : 0.05;

      this.tankColor = getHexColor( tank.color || '#8a9ba8' );
      this.oilColor  = getHexColor( this.oilColors?.[tank.oilCode] || '#d9822b' );

      this.container = this.$refs['container'];

      // 1. 씬, 카메라
      this.scene = new THREE.Scene();
      this.scene.background = new THREE.Color( getHexColor(this.bgColor) );

      // 오른쪽 상세표 레이어(detailW)에 가려지지 않도록, 캔버스보다 detailW 만큼 넓은 가상 화면에서
      // 오른쪽 detailW 를 뺀 부분(x = detailW 부터)만 그린다. 탱크가 상세표 폭의 절반만큼 왼쪽으로 밀려 보인다
      const fullW = this.w + this.detailW;
      this.camera = new THREE.PerspectiveCamera( 40, fullW / this.h, 0.1, 1000 );
      this.camera.setViewOffset( fullW, this.h, this.detailW, 0, this.w, this.h );
      // 열린 끝(-x)이 앞으로 오는 거의 정면에 가까운 시점.
      // 화면에서 OrbitControls 로 직접 맞춘 구도(16FA, fov 40, 캔버스 452x320, 상세표 155px)를
      // 기준 길이 base 에 대한 비율로 옮긴 값이다. 시선(target)은 initRenderer 에서
      // OrbitControls 에 넣는다 (controls 가 생성될 때 target 으로 lookAt 을 덮어쓰기 때문).
      const base = Math.max( this.tankL, this.tankR * 2 );
      this.camera.position.set( -base * 1.206, base * 0.231, base * 1.178 );
      this.camTarget = new THREE.Vector3( -base * 0.043, -base * 0.044, -base * 0.153 );
      this.camera.lookAt( this.camTarget );

      // 2. 조명
      // 환경광만 세면 내부 벽면이 균일하게 칠해져 평면처럼 보인다. 환경광은 낮추고
      // 위/아래 밝기가 다른 반구광 + 열린 끝 안쪽의 점광원으로 곡면을 따라 명암을 만든다.
      this.scene.add( new THREE.AmbientLight( 0xffffff, 0.3 ) );
      this.scene.add( new THREE.HemisphereLight( 0xffffff, 0x3a3a3a, 0.9 ) );
      const key = new THREE.DirectionalLight( 0xffffff, 1.6 );
      key.position.set( -base, base * 1.2, base * 1.5 );
      this.scene.add( key );
      const fill = new THREE.DirectionalLight( 0xffffff, 0.5 );
      fill.position.set( base, base * 0.3, -base );
      this.scene.add( fill );
      // 열린 끝 바로 안쪽의 점광원. 가까운 벽은 밝고 안쪽으로 갈수록 어두워져 깊이감이 생긴다.
      const inner = new THREE.PointLight( 0xffffff, base * 0.5, base * 2, 2 );
      inner.position.set( -this.tankL * 0.5 + this.tankR * 0.6, this.tankR * 0.3, 0 );
      this.scene.add( inner );

      this.openX = -this.tankL * 0.5;

      this.draw_tank_body();
      this.draw_fluid();
      this.draw_sensor();
      this.draw_thermometers();
      this.draw_safety_line();

      this.initRenderer();
    },

    /**
     * 콘크리트 알갱이 텍스처. 128x128 캔버스에 밝기가 조금씩 다른 점을 흩뿌려 만든다.
     * 색은 재질 color 와 곱해지므로 회색 계열 노이즈만 담는다.
     */
    makeConcreteTexture(){
      const size = 128;
      const canvas = document.createElement('canvas');
      canvas.width = size; canvas.height = size;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#d0d0d0';
      ctx.fillRect(0, 0, size, size);
      for(let i = 0; i < 2600; i++){
        const v = 150 + Math.floor(Math.random() * 90); // 150~240 밝기
        ctx.fillStyle = `rgb(${v},${v},${v})`;
        const r = Math.random() < 0.15 ? 2 : 1;
        ctx.fillRect( Math.random() * size, Math.random() * size, r, r );
      }
      const tex = new THREE.CanvasTexture( canvas );
      tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
      tex.repeat.set( 2, 2 );
      return tex;
    },

    /**
     * 탱크 본체. 원통 껍데기(열린 끝 쪽은 경판 없음) + 반대편 둥근 경판 + 열린 끝의 두께 링
     * + 맨홀·노즐 + 새들 지지대. 원통 축은 x 축이다.
     */
    draw_tank_body(){
      const R = this.tankR, L = this.tankL, t = this.wallT;
      const shell = { color: this.tankColor, roughness: 0.55, metalness: 0.25 };

      this.mat.outer = new THREE.MeshStandardMaterial({ ...shell, side: THREE.FrontSide });
      // 약간의 metalness 로 곡면을 따라 하이라이트가 생기게 한다
      this.mat.inner = new THREE.MeshStandardMaterial({ color: getHexColor(this.colors.lining), roughness: 0.55, metalness: 0.25, side: THREE.BackSide });
      this.mat.rim   = new THREE.MeshStandardMaterial({ ...shell, side: THREE.DoubleSide });
      // 콘크리트 질감: 무광(roughness 1, metalness 0) + 캔버스로 만든 알갱이 노이즈 텍스처
      this.tex.concrete = this.makeConcreteTexture();
      this.mat.support = new THREE.MeshStandardMaterial({
        color: getHexColor(this.colors.support), roughness: 1, metalness: 0, map: this.tex.concrete
      });

      // 원통 껍데기 (양끝 개방). 외벽은 앞면, 내벽은 뒷면만 그려 두께감을 낸다.
      // 카메라 쪽 옆면(+z) 중앙에서 위·아래 45°(총 90°)는 비워 안이 보이게 한다.
      // rotateZ(π/2) 뒤 원주 각도 θ 는 θ=0 이 +z, θ=π/2 가 +y 이므로 π/4 부터 3π/2 만큼만 만든다.
      const cutStart = PI * 0.25, cutLen = PI * 1.5;
      this.geo.outer = new THREE.CylinderGeometry( R, R, L, 64, 1, true, cutStart, cutLen );
      this.geo.outer.rotateZ( PI * 0.5 );
      this.geo.inner = new THREE.CylinderGeometry( R - t, R - t, L, 64, 1, true, cutStart, cutLen );
      this.geo.inner.rotateZ( PI * 0.5 );
      this.mesh.outer = new THREE.Mesh( this.geo.outer, this.mat.outer );
      this.mesh.inner = new THREE.Mesh( this.geo.inner, this.mat.inner );

      // 열린 끝의 벽 두께 링 (같은 90° 를 비운다). rotateY(-π/2) 뒤 각도 기준이 원통과 같다
      this.geo.rim = new THREE.RingGeometry( R - t, R, 64, 1, cutStart, cutLen );
      this.geo.rim.rotateY( PI * -0.5 ); // 법선을 -x(열린 쪽)로
      this.mesh.rim = new THREE.Mesh( this.geo.rim, this.mat.rim );
      this.mesh.rim.position.set( this.openX, 0, 0 );

      // 비운 옆면의 잘린 두 변(θ=±45°)에 벽 두께(t)만큼의 단면 판을 덧대 두께감을 유지한다.
      // 판은 x 방향 길이 L, 반지름 방향 폭 t. y 축을 해당 각도 방향으로 돌려 놓는다 (x 축 회전 = π/2 - θ)
      this.geo.cutEdge = new THREE.PlaneGeometry( L, t );
      this.geo.cutEdge.translate( 0, R - t * 0.5, 0 );
      [ cutStart, -cutStart ].forEach((theta, i)=>{
        const g = this.geo.cutEdge.clone();
        g.rotateX( PI * 0.5 - theta );
        this.geo['cutEdge' + i] = g;
        this.mesh['cutEdge' + i] = new THREE.Mesh( g, this.mat.rim );
      });

      // 반대편 둥근 경판: 밑면 반지름 R, 높이 h 인 구면 캡
      const h = R * 0.28;
      const Rs = (R * R + h * h) / (2 * h);
      const theta = Math.asin( Math.min( 1, R / Rs ) );
      this.geo.head = new THREE.SphereGeometry( Rs, 64, 24, 0, PI * 2, 0, theta );
      this.geo.head.rotateZ( PI * -0.5 ); // 극(+y)을 +x 로
      this.mesh.head = new THREE.Mesh( this.geo.head, this.mat.outer );
      this.mesh.head.position.set( L * 0.5 - (Rs - h), 0, 0 );
      // 경판 안쪽면 (열린 끝에서 들여다볼 때 보인다)
      this.mesh.headIn = new THREE.Mesh( this.geo.head, this.mat.inner );
      this.mesh.headIn.position.copy( this.mesh.head.position );

      // 맨홀 (짧고 넓은 원통 + 플랜지 원판) - 상단 중앙에서 뒤쪽으로 약간
      const mhR = R * 0.22, mhH = R * 0.14;
      this.geo.manhole = new THREE.CylinderGeometry( mhR, mhR, mhH, 32 );
      this.geo.flange  = new THREE.CylinderGeometry( mhR * 1.3, mhR * 1.3, mhH * 0.25, 32 );
      this.mesh.manhole = new THREE.Mesh( this.geo.manhole, this.mat.outer );
      this.mesh.manhole.position.set( L * 0.12, R + mhH * 0.5 - 0.01, 0 );
      this.mesh.flange = new THREE.Mesh( this.geo.flange, this.mat.rim );
      this.mesh.flange.position.set( L * 0.12, R + mhH, 0 );

      // 노즐 2개 (상단, 열린 쪽과 경판 쪽)
      const nzR = Math.max( this.nozSize, R * 0.05 ), nzH = R * 0.18;
      this.geo.nozzle = new THREE.CylinderGeometry( nzR, nzR, nzH, 16 );
      this.geo.nozzleCap = new THREE.CylinderGeometry( nzR * 1.5, nzR * 1.5, nzH * 0.2, 16 );
      [ -L * 0.32, L * 0.36 ].forEach((x, i)=>{
        const nz = new THREE.Mesh( this.geo.nozzle, this.mat.outer );
        nz.position.set( x, R + nzH * 0.5 - 0.01, 0 );
        const cap = new THREE.Mesh( this.geo.nozzleCap, this.mat.rim );
        cap.position.set( x, R + nzH, 0 );
        this.mesh['nozzle' + i] = nz;
        this.mesh['nozzleCap' + i] = cap;
      });

      // 받침대 2개: 사각 블록의 윗면을 실린더 곡면에 맞춰 오목하게 판 단면을 x 방향으로 돌출.
      //   sw: x 폭, sd: z 깊이, sh: 바닥에서 원통 최하단까지 높이
      // 단면(z,y)은 바닥 사각형 + 반지름 Ra 의 원호. 원호 중심은 탱크 축(y = sh + Ra)이다.
      // Ra 는 외벽보다 반지름의 3% 만큼 크게 잡아 탱크와 받침대 사이에 살짝 틈이 보이게 한다.
      const sw = L * 0.07, sh = R * 0.35, sd = R * 1.5;
      const Ra = R + R * 0.03;
      const yc = sh + Ra;                                   // 원호 중심 y (단면 좌표)
      const yEdge = yc - Math.sqrt( Ra * Ra - sd * sd * 0.25 ); // 받침대 양끝(z=±sd/2)에서의 윗면 높이
      const aEnd = Math.atan2( yEdge - yc,  sd * 0.5 );      // 오른쪽 끝 각도 (약 -41°)
      const aStart = Math.atan2( yEdge - yc, -sd * 0.5 );    // 왼쪽 끝 각도 (약 -139°)
      const shape = new THREE.Shape();
      shape.moveTo( -sd * 0.5, 0 );
      shape.lineTo(  sd * 0.5, 0 );
      shape.lineTo(  sd * 0.5, yEdge );
      shape.absarc( 0, yc, Ra, aEnd, aStart, true ); // 시계 방향으로 원통 아래쪽을 따라 왼쪽 끝까지
      shape.closePath();
      this.geo.support = new THREE.ExtrudeGeometry( shape, { depth: sw, bevelEnabled: false, curveSegments: 24 } );
      this.geo.support.rotateY( PI * 0.5 ); // 단면의 가로(z)·돌출(x) 방향 맞춤
      [ -L * 0.3, L * 0.3 ].forEach((x, i)=>{
        const sp = new THREE.Mesh( this.geo.support, this.mat.support );
        sp.position.set( x - sw * 0.5, -yc, 0 ); // 원호 중심을 탱크 축(y=0)에 맞춘다
        this.mesh['support' + i] = sp;
      });

      Object.keys( this.mesh ).forEach(k=>{ if( this.mesh[k] ) this.scene.add( this.mesh[k] ); });
    },

    /**
     * 유량/수분 도형. 닫힌 원통을 수위 평면으로 잘라 열린 끝에서 원형 조각 단면이 보이게 한다.
     *   수분 : y <= wtrY
     *   유량 : wtrY <= y <= oilY
     * 잘린 윗면은 뚫려 있으므로 유면판(setLevelSurface)으로 덮는다.
     */
    draw_fluid(){
      const R = this.tankR - this.wallT - 0.01;
      const L = this.tankL - this.wallT * 2;

      this.lvl.oilTop = keepBelowY( -R );
      this.lvl.oilBtm = keepAboveY( -R );
      this.lvl.wtrTop = keepBelowY( -R );

      const fluidGeo = () => {
        const g = new THREE.CylinderGeometry( R, R, L, 64, 1, false );
        g.rotateZ( PI * 0.5 );
        return g;
      };
      this.geo.oil = fluidGeo();
      this.geo.wtr = fluidGeo();

      // 유체는 반투명으로 그려 안쪽 센서 배관과 아래 수분층이 비쳐 보이게 한다.
      // depthWrite 를 끄면 반투명끼리 순서에 따라 사라지는 현상이 줄어든다.
      // FrontSide 만 그린다. 뒷면까지 그리면 반투명 너머로 안쪽 벽면이 겹쳐 줄무늬처럼 보인다.
      const fluidMat = (color, planes, opacity) => new THREE.MeshStandardMaterial({
        color, roughness: 0.4, metalness: 0.05, side: THREE.FrontSide,
        transparent: true, opacity, depthWrite: false,
        clippingPlanes: planes, clipIntersection: false // 하나라도 벗어나면 제거
      });
      this.mat.oil = fluidMat( this.oilColor, [ this.lvl.oilTop, this.lvl.oilBtm ], this.fluidOpacity );
      this.mat.wtr = fluidMat( getHexColor(this.colors.water), [ this.lvl.wtrTop ], this.wtrOpacity );

      this.mesh.oil = new THREE.Mesh( this.geo.oil, this.mat.oil );
      this.mesh.wtr = new THREE.Mesh( this.geo.wtr, this.mat.wtr );
      this.scene.add( this.mesh.oil );
      this.scene.add( this.mesh.wtr );

      // 유면·수면 판. 폭은 그 높이의 원통 단면 폭이라 패킷마다 다시 만든다 (setLevelSurface)
      const surfMat = (color, opacity) => new THREE.MeshStandardMaterial({
        color, roughness: 0.25, side: THREE.DoubleSide,
        transparent: true, opacity, depthWrite: false
      });
      this.mat.oilSurf = surfMat( this.oilColor, this.fluidOpacity );
      this.mat.wtrSurf = surfMat( getHexColor(this.colors.water), this.wtrOpacity );
      this.mesh.oilSurf = new THREE.Mesh( new THREE.BufferGeometry(), this.mat.oilSurf );
      this.mesh.wtrSurf = new THREE.Mesh( new THREE.BufferGeometry(), this.mat.wtrSurf );
      this.mesh.oilSurf.visible = false;
      this.mesh.wtrSurf.visible = false;
      this.scene.add( this.mesh.oilSurf );
      this.scene.add( this.mesh.wtrSurf );
    },

    /**
     * 수면판 갱신. y 높이에서의 원통 단면 폭(2·sqrt(R²-y²)) × 유체 길이 크기의 판을 y 에 둔다.
     * @param key 'oilSurf' | 'wtrSurf'
     * @param y   수면 높이 (탱크 중심 기준, m)
     * @param R   유체 반지름
     */
    setLevelSurface(key, y, R){
      const m = this.mesh[key];
      if( !m ) return;
      m.geometry?.dispose();
      const half = Math.sqrt( Math.max( 0, R * R - y * y ) );
      if( half < 0.01 || y <= -R + 0.001 ){
        m.geometry = new THREE.BufferGeometry();
        m.visible = false;
        return;
      }
      const g = new THREE.PlaneGeometry( this.tankL - this.wallT * 2, half * 2 );
      g.rotateX( PI * -0.5 );
      m.geometry = g;
      m.position.set( 0, y, 0 );
      m.visible = true;
    },

    /**
     * 센서 배관과 센서 헤드. 배관은 탱크 바닥에서 위로 관통해 열린 끝에서 보이고(Tank2D 와 같은 자리),
     * 헤드는 탱크 위로 올라온다. 배관은 열린 끝(전면)에서 약간 안쪽에 두어 잘 보이게 한다.
     */
    draw_sensor(){
      const R = this.tankR;
      // 배관이 탱크 위로 솟는 길이는 반지름의 15% 로 제한한다. 실제 센서봉(sensorLen)은
      // 라이저를 관통해 더 길지만, 그대로 그리면 헤드가 카드 캔버스 위로 벗어난다.
      const senH = Math.min( Math.max( this.senH, R * 2 + 0.1 ), R * 2.15 );
      this.geo.pipe = new THREE.CylinderGeometry( this.nozSize, this.nozSize, senH, 24 );
      // 센서 헤드: 배관 굵기의 5배 반지름, 12배 높이 (배관 위에 얹힌다)
      this.geo.sensor = new THREE.CylinderGeometry( this.nozSize * 5, this.nozSize * 5, this.nozSize * 12, 16 );
      this.mat.pipe = new THREE.MeshPhongMaterial({ color: getHexColor(this.colors.pipe) });
      this.mat.sensor = new THREE.MeshPhongMaterial({ color: getHexColor(this.colors.sensor.off) });

      // 열린 끝(전면)에서 반지름의 절반만큼 안쪽. 단면 바로 뒤에 있어 반투명 유체 너머로 보인다.
      this.pipeX = this.openX + this.tankR * 0.5;
      this.mesh.pipe = new THREE.Mesh( this.geo.pipe, this.mat.pipe );
      this.mesh.pipe.position.set( this.pipeX, -R + senH * 0.5, 0 );
      this.mesh.sensor = new THREE.Mesh( this.geo.sensor, this.mat.sensor );
      this.mesh.sensor.position.set( this.pipeX, -R + senH + this.nozSize * 6, 0 );
      this.scene.add( this.mesh.pipe );
      this.scene.add( this.mesh.sensor );

      // 센서봉의 플로트 원판 2개. 유량 플로트는 유면 바로 위, 수분 플로트는 수면 바로 위에 뜬다.
      // 위치는 패킷마다 renderVolume() 이 옮긴다.
      // 반지름은 배관 굵기의 12.8배(처음 3.2배의 4배), 두께는 1.6배
      const fr = this.nozSize * 12.8, fh = Math.max( 0.06, this.nozSize * 1.6 );
      this.floatH = fh;
      this.geo.float = new THREE.CylinderGeometry( fr, fr, fh, 32 );
      // 유량 플로트는 금속 질감(스테인리스), 수분 플로트는 네이비
      this.mat.floatOil = new THREE.MeshStandardMaterial({ color: getHexColor(this.colors.floatOil), metalness: 0.9, roughness: 0.3 });
      this.mat.floatWtr = new THREE.MeshStandardMaterial({ color: getHexColor(this.colors.floatWtr), metalness: 0.2, roughness: 0.6 });
      this.mesh.floatOil = new THREE.Mesh( this.geo.float, this.mat.floatOil );
      this.mesh.floatWtr = new THREE.Mesh( this.geo.float, this.mat.floatWtr );
      this.mesh.floatOil.position.set( this.pipeX, -R + fh * 0.5 + 0.01, 0 );
      this.mesh.floatWtr.position.set( this.pipeX, -R + fh * 0.5 + 0.01, 0 );
      this.scene.add( this.mesh.floatOil );
      this.scene.add( this.mesh.floatWtr );
    },

    /** 온도계. 배관 위 각 온도센서 위치(location, mm)에 작은 원판으로 표시 (Tank3D 와 동일 규칙) */
    draw_thermometers(){
      const temps = this.machine?.temps || [];
      if( !temps.length ) return;
      this.geo.tm = new THREE.CylinderGeometry( this.nozSize * 2, this.nozSize * 2, 0.08, 24, 1 );
      temps.forEach(t=>{
        const tmKey = `tm${t.idx}`;
        const tmH = -this.tankR + (t.location * 0.001);
        this.tmMater[tmKey] = new THREE.MeshPhongMaterial({ color: getHexColor(this.colors.tm.off) });
        this.tmMesh[tmKey] = new THREE.Mesh( this.geo.tm, this.tmMater[tmKey] );
        this.tmMesh[tmKey].position.set( this.pipeX, tmH, 0 );
        this.scene.add( this.tmMesh[tmKey] );
      });
    },

    /**
     * 적정재고량(안전재고) 표시선. 안전재고 수면이 내벽과 만나는 양쪽 수평 점선과
     * 양 끝(열린 끝·경판 앞) 안쪽을 가로지르는 점선을 탱크 안쪽에 그린다. (종전의 반투명 수면판은 뺐다)
     * 부피→유위 환산은 총용량 대비 선형 근사다 (Tank3D 2형과 동일).
     */
    draw_safety_line(){
      const safetyVol = Number(this.tank?.safetyVolume) || 0;
      const totalVol = Number(this.tank?.tankVolume) || 0;
      if( !(safetyVol > 0 && totalVol > 0) ) return;

      const rate = Math.min( 1, safetyVol / totalVol );
      const R = this.tankR;
      const y = -R + rate * R * 2;
      // 탱크 안쪽(내벽 바로 안)에 그린다. 열린 끝과 측면 창으로 보인다
      const Ri = R - this.wallT - 0.02;
      const x = Math.sqrt( Math.max( 0.01, Ri * Ri - y * y ) );
      const lh = this.tankL * 0.5 - 0.01; // 옆 점선은 원통 끝(경판 시작)까지

      // 굵은 점선. WebGL 은 기본 Line 의 두께를 1px 로 고정하므로 LineSegments2 + LineMaterial 을 쓴다.
      // linewidth 는 픽셀 단위이고 resolution 에 렌더러 크기를 넣어야 두께가 맞는다.
      this.mat.safety = new LineMaterial({
        color: 0xf86c6b,
        linewidth: 3,          // px
        dashed: true,
        dashSize: 0.08,        // 촘촘한 점선 (월드 단위)
        gapSize: 0.05,
      });
      this.mat.safety.resolution.set( this.w, this.h );
      // 양 옆 내벽을 따라가는 길이 방향 점선 2개 + 정면(열린 끝)과 반대편 끝(경판 앞) 안쪽 원을
      // 가로지르는 현(chord) 점선 각 1개
      const fx = this.openX + 0.05;       // 열린 끝 안쪽
      const bx = this.tankL * 0.5 - 0.05; // 반대편 경판 바로 앞
      this.geo.safety = new LineSegmentsGeometry().setPositions([
        -lh, y,  x,   lh, y,  x,
        -lh, y, -x,   lh, y, -x,
         fx, y, -x,   fx, y,  x,
         bx, y, -x,   bx, y,  x,
      ]);
      const line = new LineSegments2( this.geo.safety, this.mat.safety );
      line.computeLineDistances(); // 점선 재질은 이 호출이 있어야 파선으로 그려진다
      this.mesh.safety = line;
      this.scene.add( line );
    },

    initRenderer(){
      this.renderer = new THREE.WebGLRenderer({ antialias: true });
      this.renderer.setSize( this.w, this.h );
      this.renderer.setPixelRatio( window.devicePixelRatio );
      this.renderer.localClippingEnabled = true; // 수위 클리핑 활성화
      this.container.appendChild( this.renderer.domElement );

      // 매 프레임 그리지 않고 바뀔 때만 그린다 (Tank3D 와 동일). enableDamping 은 관성 때문에
      // 계속 그려야 하므로 쓰지 않는다.
      this.controls = new OrbitControls( this.camera, this.renderer.domElement );
      this.controls.target.copy( this.camTarget );
      this.controls.update();
      this.controls.addEventListener( 'change', this.render );

      this.render();
    },

    changeObjectColor(obj, color){
      if(!obj) return;
      obj.color.set( getHexColor(color) );
      this.render();
    },

    /** 센서 시그널 표시 */
    sensorBlink(status='on'){
      this.changeObjectColor( this.mat.sensor, this.colors.sensor[status] || this.colors.sensor['unknown'] );
      setTimeout(()=>{ this.changeObjectColor( this.mat.sensor, this.colors.sensor.off ); }, 500);
    },

    /** 온도센서 시그널 표시 */
    tempBlink(tmKey, status='on'){
      this.changeObjectColor( this.tmMater[tmKey], this.colors.tm[status] || this.colors.tm['unknown'] );
      this.tmUseMap[tmKey] = true;
      setTimeout(()=>{
        this.changeObjectColor( this.tmMater[tmKey], this.colors.tm.off );
        this.tmUseMap[tmKey] = false;
      }, 4500);
    },

    /**
     * 플로트 원판을 액면 위로 옮긴다. 원판 바닥이 액면에 닿도록 반높이만큼 띄운다.
     * 액면이 바닥이면(빈 탱크) 탱크 바닥 위에 놓이고, 두 플로트가 같은 높이면 수분 플로트를 아래로 둔다.
     * @param oilY 유면 높이 (탱크 중심 기준)
     * @param wtrY 수면 높이
     * @param R    유체 반지름
     */
    setFloats(oilY, wtrY, R){
      if( !this.mesh.floatOil || !this.mesh.floatWtr ) return;
      const h = this.floatH || 0.03;
      const floor = -R + h * 0.5 + 0.01;
      let wy = Math.max( floor, wtrY + h * 0.5 );
      let oy = Math.max( floor, oilY + h * 0.5 );
      if( oy - wy < h ) oy = wy + h; // 겹치지 않게 유량 플로트를 한 장 위로
      this.mesh.floatWtr.position.y = wy;
      this.mesh.floatOil.position.y = oy;
    },

    /**
     * 부피 비율(0~1)을 누운 원통의 액면 높이(0~2R)로 바꾼다.
     * 원의 활꼴 넓이 A(h) = R²/2·(θ - sinθ), θ = 2·acos(1 - h/R) 를 이분법으로 뒤집는다.
     * Tank2D 는 부피 비율을 높이에 선형으로 놓지만, 3D 에서는 그려진 활꼴의 부피가
     * 그 비율과 같아지도록 정확히 환산한다 (충족률 83% 면 도형 부피도 83%).
     * @param frac 부피 비율
     * @param R    유체 반지름
     * @return 바닥에서 액면까지 높이
     */
    fracToHeight(frac, R){
      const f = Math.min( 1, Math.max( 0, Number(frac) || 0 ) );
      if( f <= 0 ) return 0;
      if( f >= 1 ) return R * 2;
      const full = PI * R * R;
      let lo = 0, hi = R * 2;
      for( let i = 0; i < 40; i++ ){
        const h = (lo + hi) * 0.5;
        const th = 2 * Math.acos( 1 - h / R );
        const area = R * R * 0.5 * (th - Math.sin(th));
        if( area / full < f ) lo = h; else hi = h;
      }
      return (lo + hi) * 0.5;
    },

    /**
     * 패킷으로 클리핑 평면 상수만 갱신한다.
     * 액면 높이는 Tank2D 와 같은 기준(부피 ÷ 총용량)으로 잡는다. 유위(ohr)를 그대로 쓰면
     * tankHeight 와 총용량이 맞지 않는 탱크에서 충족률과 그림이 어긋난다.
     *   전체 액면 : ov(수분 포함 유량) ÷ tankVolume  (= 충족률 tvp)
     *   수분 액면 : wv ÷ tankVolume
     * 총용량이 없으면 유위·수위(mm)를 그대로 쓴다.
     * @param tp 패킷 payload
     */
    renderVolume(tp){
      if( !this.renderer || !this.lvl.oilTop ) return;
      const R = this.tankR - this.wallT - 0.01;
      const clampY = (mm) => Math.min( R, Math.max( -R, -this.tankR + (Number(mm) || 0) / 1000 ) );

      const totalVol = Number(this.tank?.tankVolume) || 0;
      let oilY, wtrY;
      if( totalVol > 0 ){
        const wv = Number(tp.wv) || 0;
        const ov = Number(tp.ov) || ( (Number(tp.ovm) || 0) + wv ); // ov 가 없으면 실유량+수분량
        oilY = -R + this.fracToHeight( ov / totalVol, R );
        wtrY = -R + this.fracToHeight( wv / totalVol, R );
      }else{
        oilY = clampY( tp.ohr );
        wtrY = clampY( tp.whr );
      }
      if( wtrY > oilY ) oilY = wtrY;

      // keepBelowY(y): constant = y, keepAboveY(y): constant = -y
      this.lvl.oilTop.constant = oilY;
      this.lvl.oilBtm.constant = -wtrY;
      this.lvl.wtrTop.constant = wtrY;

      this.setLevelSurface( 'oilSurf', oilY, R );
      this.setLevelSurface( 'wtrSurf', wtrY, R );
      this.setFloats( oilY, wtrY, R );
      // 유면과 수면이 같은 높이면 유면판을 감춘다 (z-fighting 방지)
      if( oilY - wtrY < 0.002 && this.mesh.oilSurf ) this.mesh.oilSurf.visible = false;

      this.sensorBlink( this.status );

      (tp.useTms || []).forEach(i=>{
        const tmKey = 'tm'+i;
        if( this.tmMater[tmKey] ) setTimeout( ()=>{ this.tempBlink(tmKey) }, i*50 );
      });

      this.render();
    },

    /**
     * 컨테이너 폭이 바뀌었을 때(큰 글씨 모드 전환 등) 렌더러 크기·카메라 종횡비·뷰 오프셋·
     * 굵은 선 해상도를 다시 맞춘다.
     */
    onResize(){
      if( !this.renderer || !this.camera || !this.container ) return;
      const availW = this.container.clientWidth;
      if( !(availW > 0) ) return;
      this.w = availW;
      this.renderer.setSize( this.w, this.h );
      const fullW = this.w + this.detailW;
      this.camera.aspect = fullW / this.h;
      this.camera.setViewOffset( fullW, this.h, this.detailW, 0, this.w, this.h );
      this.camera.updateProjectionMatrix();
      if( this.mat.safety?.resolution ) this.mat.safety.resolution.set( this.w, this.h );
      this.render();
    },

    render(){
      if(this.renderer) this.renderer.render( this.scene, this.camera );
    },

    destroyObjects(){
      if(!this.renderer) return;
      this.renderer.setAnimationLoop(null);

      Object.keys( this.mesh ).forEach(k=>{
        if(this.mesh[k]){
          this.scene.remove( this.mesh[k] );
          // 수면판은 지오메트리를 this.geo 가 아닌 메쉬가 직접 들고 있다
          if( /Surf$/.test(k) ) this.mesh[k].geometry?.dispose();
        }
      });
      Object.keys( this.tmMesh ).forEach(k=>{ if(this.tmMesh[k]) this.scene.remove( this.tmMesh[k] ); });
      Object.keys( this.geo ).forEach(k=>{ if(this.geo[k]) this.geo[k].dispose(); });
      Object.keys( this.mat ).forEach(k=>{ if(this.mat[k]) this.mat[k].dispose(); });
      Object.keys( this.tex ).forEach(k=>{ if(this.tex[k]) this.tex[k].dispose(); });
      Object.keys( this.tmMater ).forEach(k=>{ if(this.tmMater[k]) this.tmMater[k].dispose(); });

      if(this.controls){
        this.controls.removeEventListener('change', this.render);
        this.controls.dispose();
        this.controls = null;
      }

      // 캔버스를 떼고 WebGL 컨텍스트를 반납한다 (브라우저 컨텍스트 상한 대비, Tank3D 와 동일)
      const canvasEl = this.renderer.domElement;
      if(canvasEl && canvasEl.parentNode) canvasEl.parentNode.removeChild(canvasEl);
      this.renderer.forceContextLoss();
      this.renderer.dispose();
      this.renderer = null;
    },

    /**
     * call from parent component (TankMonitor.atgDataHandler)
     * @param data {payload, inout}
     * @param init 최초 적재 여부
     */
    setVolume(data, init = false){
      let tp;
      try {
        tp = data.payload;
        this.pk = tp;
        this.lastDt = this.toOnlyTime( tp.dt );

        // 최초 적재(init)는 pk/lastDt 만 반영한다. 도형은 mounted() 가 renderVolume() 으로 그린다.
        if( init ) return;

        this.badgeColor = clone(_normalBadge);
        this.status = 'on'

        if (tp.isEvt || tp.isErr) {
          this.status = 'warn';
          this.signalColor = tp.isErr ? 'danger' : 'warning';
          this.events.data = tp.events;
          this.events.dt = tp.dt;
        }else{
          this.events.data = [];
          this.events.dt = tp.dt;
          this.signalColor = 'info';
        }

        this.widgetInstance?.setEvents(this.events);

        this.renderVolume(tp);

        this.inout = data.inout? data.inout: {sts: 'N'};
        this.applyInoutSts( data.inout );
        this.checkVolumeDown( tp ); // 입출고 없이 재고가 줄면 상태 뱃지를 유량감소로 덮는다

        setTimeout(() => { this.signalColor = "dark" }, 2500); // 패킷 수신 표시(테두리 색) 2.5초 유지
      }catch(err){
        console.error(err);
      }finally{
        tp = null;
      }
    },

    async getInout(){
      const rs = await apiCall('get', `/api/inout/current/${this.tank.tid}`);
      if(rs.code===200){
        this.inout = rs.result;
        if( this.inout.sts!=='N') this.inputTargetVol = Math.round( ltrToUnit(this.inout.targetVol, this.volUnit) );
      }
    },

    async setInout( gbn ){
      let cmd = 'B';
      let cmdName = '시작';
      try{
        const inoutStr = IoStsMap[gbn];

        if(gbn==='N'){
          if( !await this.confirmModal(`${this.tnm} ${inoutStr} 작업 취소 확인`, `${inoutStr} 작업 취소`) ) return;
          cmd = 'C';
          cmdName = '취소'
        }else if(this.inout?.sts!=='N') {
          cmd = 'E'
          cmdName = '종료';
        }

        const param = { reqSts: gbn, cmd: cmd };
        const rs = await apiCall('post', `/api/inout/${this.tank.tid}`, param);
        if(rs.code===200){
          this.inout = rs.result;

          if( gbn==='I' ) this.inputTargetVol = Math.floor( ltrToUnit(this.pk.vf, this.volUnit) );
          else if( gbn==='O') this.inputTargetVol = Math.floor( ltrToUnit(this.pk.ovm, this.volUnit) );

          this.applyInoutSts(this.inout);

          const msg = `${this.tnm}, ${inoutStr} ${cmdName}, 설정 완료`;
          await this.toastInfo(msg , '경고');
          speech(msg);
        }else{
          await this.toastWarn(rs.message, '경고');
        }
      }catch(err){
        console.error(err);
        await this.toastError(err);
      }
    },

    async setInoutVolume(){
      try{
        const gbn = this.inout.sts;
        if(!this.inputTargetVol){
          return await this.toastInfo('기준량 입력 오류 ', '경고');
        }

        const inoutStr = (gbn==='I')? '입고':'출고';
        const unitStr = `${unitSign(this.volUnit)} 단위로`;
        const targetVol = Number(this.inputTargetVol);

        // 선택 단위를 리터로 환산해 보낸다 (서버는 l/g 만 해석)
        const param = { cmd : 'V', reqSts: gbn, volume: unitToLtr(targetVol, this.volUnit), unit: 'l' };
        const rs = await apiCall('post', `/api/inout/${this.tank.tid}`, param);

        if(rs.code===200){
          this.inout = rs.result;
          this.applyInoutSts(this.inout);
          const msg = `탱크 ${this.tank.name}, ${inoutStr} 작업 기준량, ${this.inputTargetVol} ${unitStr} 설정 되었습니다.`;
          await this.toastInfo(msg, '경고');
          speech(msg)
        }else{
          await this.toastWarn(rs.message, '경고');
        }
      }catch(err){
        console.error(err);
      }
    },

    /** 온도센서 표 펼치기/닫기 (평균온도 라벨 클릭) */
    toggleDetail(){
      this.detailTmShow = !this.detailTmShow;
    },

    // 상단 단위 배지 클릭: 해당 단위만 전환
    toggleVolUnit(){
      // L → kL → 드럼(D, 200L) → gal → L 순환
      const order = ['l', 'k', 'd', 'g'];
      this.volUnit = order[ (order.indexOf(this.volUnit) + 1) % order.length ];
      // 진행 중인 입출고 목표량 입력값도 새 단위로 다시 표시
      if( this.inout && this.inout.sts && this.inout.sts !== 'N' && this.inout.targetVol ){
        this.inputTargetVol = Math.round( ltrToUnit(this.inout.targetVol, this.volUnit) );
      }
    },
    toggleLenUnit(){ this.lenUnit = (this.lenUnit === 'mm') ? 'i' : 'mm'; },
    toggleTmpUnit(){ this.tmpUnit = (this.tmpUnit === 'c') ? 'f' : 'c'; },
  },

  watch: {
    // 큰 글씨 모드 전환으로 카드 폭이 바뀌면 렌더러 크기와 카메라 오프셋을 다시 맞춘다
    '$store.state.largeFont'(){
      this.$nextTick(() => this.onResize());
    },
  },

  beforeDestroy(){
    this.destroyObjects();
  },
}
</script>
